
import React, { useState } from "react";
import { Box, Typography, Button, Paper, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import BodyMap from "./BodyMap";
import BatteryLevel from "./BatteryLevel";
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const C = {
  bg: '#f5f5fa',
  white: '#fff',
  sf2: '#eeeff5',
  bd: '#dddde6',
  teal: '#0d9488',
  tealL: '#14b8a6',
  tealBg: 'rgba(13,148,136,.07)',
  tealGlow: 'rgba(13,148,136,.22)',
  purple: '#7c3aed',
  purpleBg: 'rgba(124,58,237,.07)',
  purpleGlow: 'rgba(124,58,237,.18)',
  tx: '#18162a',
  tx2: '#5e5c72',
  tx3: '#908ea3',
};




export const drains = [
  {
    id: 'exams',
    icon: '📝',
    label: 'Exams & Deadlines',
    category: 'performance',
    insight: 'Academic pressure overloading working memory and focus capacity'
  },
  {
    id: 'grades',
    icon: '📊',
    label: 'Grades & Performance',
    category: 'performance',
    insight: 'Achievement anxiety creating constant self-evaluation stress'
  },
  {
    id: 'social',
    icon: '👥',
    label: 'Social Pressure',
    category: 'emotional',
    insight: 'Peer comparison and social dynamics consuming emotional energy'
  },
  {
    id: 'sleep_d',
    icon: '😴',
    label: 'Poor Sleep',
    category: 'recovery',
    insight: 'Irregular sleep patterns preventing full mental recovery'
  },
  {
    id: 'screen',
    icon: '📱',
    label: 'Phone & Social Media',
    category: 'stimulation',
    insight: 'Doomscrolling and notifications fragmenting attention and rest'
  },
  {
    id: 'lonely',
    icon: '🏠',
    label: 'Loneliness / Homesick',
    category: 'connection',
    insight: 'Being away from support systems depleting emotional reserves'
  },
  {
    id: 'future',
    icon: '🔮',
    label: 'Career Uncertainty',
    category: 'psychological',
    insight: 'Future anxiety — energy lost worrying about what comes next'
  },
  {
    id: 'money',
    icon: '💰',
    label: 'Money Worries',
    category: 'psychological',
    insight: 'Financial stress running as constant background drain'
  },
  {
    id: 'relation',
    icon: '💔',
    label: 'Relationship Stress',
    category: 'emotional',
    insight: 'Romantic or friendship tension consuming mental bandwidth'
  },
  {
    id: 'self',
    icon: '🪞',
    label: 'Self-Doubt',
    category: 'connection',
    insight: 'Imposter feelings and low confidence eroding inner energy'
  }
];  

const AssessmentFlow = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [battery, setBattery] = useState(50);
  const [bodyData, setBodyData] = useState({});
  const [selected, setSelected] = useState([]);

  const toggleDrain = (item) => {
    setSelected((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const stepLabels = { 1: 'Body Scan', 2: 'Energy Level', 3: "What's Draining You" };

  return (
    <Box sx={{
      background: C.bg,
      minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)', md: 'calc(100vh - 70px)' },
      py: { xs: 3, sm: 4 },
    }}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        px: 2, position: 'relative',
      }}>
        {/* Top bar — now spans the full container width so branding/back button
            sit at the corners on wider (laptop) screens instead of hugging the card */}
        <Box sx={{
          width: '100%', maxWidth: 1200,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{
              width: 42, height: 42, borderRadius: '13px',
              background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
              boxShadow: `0 4px 16px ${C.tealGlow}, 0 4px 16px ${C.purpleGlow}`,
              flexShrink: 0,
            }}>
              🌟
            </Box>
            <Box>
              <Typography sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1.2,
                background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                echoShine
              </Typography>
              <Typography sx={{
                fontSize: 11,
                color: C.tx2,
                letterSpacing: '.3px',
                lineHeight: 1.2,
              }}>
                Student Wellness Assessment
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={() => navigate("/echo-sounds")}
            sx={{
              backgroundColor: '#F0F5FF',
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: '50%',
              color: '#0B2D6B',
              flexShrink: 0,
              '&:hover': { backgroundColor: '#E0EBFF' },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Progress dots — matches .progress / .prog-dot */}
        <Box sx={{ display: 'flex', gap: '5px', width: '100%', maxWidth: 420, px: '4px', mb: 1 }}>
          {[1, 2, 3].map((s) => (
            <Box
              key={s}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: '2px',
                transition: '0.4s',
                background:
                  step === s
                    ? `linear-gradient(90deg, ${C.teal}, ${C.purple})`
                    : step > s
                      ? C.teal
                      : C.sf2,
              }}
            />
          ))}
        </Box>

        {/* Step label */}
        <Typography sx={{
          width: '100%', maxWidth: 420, px: '4px', mb: 1.5,
          fontSize: 11, fontWeight: 700, color: C.tx3,
          textTransform: 'uppercase', letterSpacing: '1px',
        }}>
          Part {step} — {stepLabels[step]}
        </Typography>

        {/* Card */}
        <Paper sx={{
          borderRadius: '22px',
          border: `1px solid ${C.bd}`,
          boxShadow: '0 2px 12px rgba(0,0,0,.04)',
          width: { xs: '100%', sm: '90%', md: 420 },
          maxWidth: 420,
          overflow: 'hidden',
        }}>
          {step === 1 && (
            <Box sx={{ p: { xs: '18px 18px 6px', sm: '22px 22px 8px' } }}>
              <Typography sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: { xs: 17, sm: 19 },
                fontWeight: 700,
                color: C.tx,
                mb: '4px',
              }}>
                Where do you feel tension?
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.tx2, lineHeight: 1.5 }}>
                Tap body areas: 1st tap = mild, 2nd = moderate, 3rd = intense, 4th = clear. Skip areas that feel fine.
              </Typography>
              <BodyMap selected={bodyData} setSelected={setBodyData} />
            </Box>
          )}

          {step === 2 && (
            <Box sx={{ p: { xs: '18px 18px 6px', sm: '22px 22px 6px' } }}>
              <Typography sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: { xs: 17, sm: 19 },
                fontWeight: 700,
                color: C.tx,
                mb: '4px',
              }}>
                How full is your mental battery?
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.tx2, lineHeight: 1.5 }}>
                Drag the slider to where you feel you are right now.
              </Typography>
              <Box textAlign="center" my={3}>
                <BatteryLevel value={battery} setValue={setBattery} />
              </Box>
            </Box>
          )}

          {step === 3 && (
            <Box sx={{ p: { xs: '18px 18px 6px', sm: '22px 22px 6px' } }}>
              <Typography sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: { xs: 17, sm: 19 },
                fontWeight: 700,
                color: C.tx,
                mb: '4px',
              }}>
                What's consuming your energy?
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.tx2, lineHeight: 1.5, mb: 1.5 }}>
                Tap all that apply — or skip if you're fully charged!
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', pb: 2 }}>
                {drains.map((d) => {
                  const isSelected = selected.includes(d.id);
                  return (
                    <Box
                      key={d.id}
                      onClick={() => toggleDrain(d.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 18px',
                        borderRadius: '40px',
                        border: `1.5px solid ${isSelected ? C.teal : C.bd}`,
                        background: isSelected
                          ? `linear-gradient(135deg, ${C.tealBg}, ${C.purpleBg})`
                          : C.white,
                        fontSize: 13,
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? C.tx : C.tx2,
                        boxShadow: isSelected ? `0 2px 12px ${C.tealGlow}` : 'none',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: '0.3s',
                        '&:hover': { borderColor: C.tealL, color: C.tx },
                        '&:active': { transform: 'scale(0.95)' },
                      }}
                    >
                      <Box component="span" sx={{ fontSize: 18 }}>{d.icon}</Box>
                      {d.label}
                    </Box>
                  );
                })}
              </Box>

              <Typography sx={{ textAlign: 'center', fontSize: 13, color: C.tx3, pb: 1 }}>
                {selected.length === 0 ? (
                  'No drains selected'
                ) : (
                  <>
                    <Box component="strong" sx={{ color: C.teal, fontWeight: 700 }}>{selected.length}</Box>
                    {' '}{selected.length === 1 ? 'drain' : 'drains'} selected
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Buttons */}
        <Box sx={{
          display: 'flex', gap: '10px', mt: '14px',
          width: { xs: '100%', sm: '90%', md: 420 }, maxWidth: 420,
        }}>
          <Button
            onClick={() => (step === 1 ? navigate("/decode-stress") : setStep(step - 1))}
            sx={{
              flex: '0 0 auto',
              padding: '15px 20px',
              borderRadius: '14px',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              background: C.white,
              color: C.tx2,
              border: `1.5px solid ${C.bd}`,
              '&:hover': { background: C.sf2 },
            }}
          >
            Back
          </Button>

          <Button
            onClick={() => {
              if (step < 3) {
                setStep(step + 1);
              } else {
                navigate("/result", { state: { bodyData, battery, selectedDrains: selected } });
              }
            }}
            sx={{
              flex: 1,
              padding: '15px 20px',
              borderRadius: '14px',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              color: '#fff',
              background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`,
              boxShadow: `0 4px 16px ${C.purpleGlow}`,
              transition: '0.25s',
              '&:hover': {
                background: `linear-gradient(135deg, ${C.teal}, ${C.purple})`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 28px ${C.purpleGlow}`,
              },
            }}
          >
            {step === 1 && "Next — Energy Battery"}
            {step === 2 && "Next — Energy Drains"}
            {step === 3 && "See My Results"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentFlow;